
import { createApp, type App } from 'vue';
import { i18n } from './src/i18n';
import { isUserLogin } from '@eureka/utils/account'
import EditionUpgradeWrap from './src/components/EditionUpgradeWrap.vue';
import type { EditionUpgradeAction, EditionUpgradeOptions } from './src/types';
import type { UsageAmountWhere } from './src/constants';
import { EurekaVersion, UpgradeUsageType } from './src/constants';
import { controlDeferred } from '@eureka/utils/control';
import { loginSDK } from '@eureka/shared/login-sdk';
import { getIfHasTrialPackage, packageInfoDeferred } from '@eureka/utils/package-info';
import { EurekaPackageType } from '@eureka/services/src/apis/common.api.types';
import { REGION } from '@eureka/utils/environment';
import { DeployEnv } from '@patsnap-utils/biz'
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { injectService } from '@patsnap/tahiti-core';
import { get } from 'lodash';

// LS PLG 相关 composable
export { useLsEditionUsage } from './src/composables/use-ls-edition-usage';

const isOverseas = [DeployEnv.US, DeployEnv.EU].includes(REGION);

export function getPackageTypeByVersion(version: EurekaVersion): EurekaPackageType {
    switch (version) {
    case EurekaVersion.AIR:
        return isOverseas ? EurekaPackageType.AIR_COMMERCIAL : EurekaPackageType.AIR_COMMERCIAL_PROFESSIONAL_EDITION;
    case EurekaVersion.INSIGHTS:
        return isOverseas ? EurekaPackageType.INSIGHTS_COMMERCIAL : EurekaPackageType.AIR_COMMERCIAL_NAVIGATOR_EDITION;
    case EurekaVersion.IP:
        return EurekaPackageType.IP_COMMERCIAL;
    case EurekaVersion.PROTECT:
        return EurekaPackageType.PROTECT_COMMERCIAL;
    case EurekaVersion.LS:
        return EurekaPackageType.LS_COMMERCIAL;
    default:
        return EurekaPackageType.AIR_COMMERCIAL;
    }
}

let isEditionUpgradeOpen = false;

// 打开升级弹窗
export function openEditionUpgrade(options: EditionUpgradeOptions): Promise<EditionUpgradeAction> {
    if (isEditionUpgradeOpen) {
        // 防止弹出多个
        return Promise.resolve('exist');
    }
    isEditionUpgradeOpen = true;
    const div = document.createElement('div');
    div.setAttribute('data-theme', 'patsnapRD');
    div.classList.add('edition-upgrade-wrap');
    document.body.appendChild(div);

    const unmountApp = (app: App) => {
        app.unmount();
        document.body.removeChild(div);
    };

    const promise = new Promise<EditionUpgradeAction>((resolve) => {
        const app = createApp(EditionUpgradeWrap, {
            ...options,
            onClose: (action: EditionUpgradeAction = 'close') => {
                resolve(action);
                unmountApp(app);
                isEditionUpgradeOpen = false;
            },
        });
        app.use(i18n);
        app.mount(div);
    });

    return promise;
}

// 试用结束
export function prepareFirstLoginAfterTrial(useWhere: UsageAmountWhere, packageType: EurekaPackageType) {
    openEditionUpgrade({ usageType: UpgradeUsageType.TRIAL_END, useWhere, packageType });
}

export async function invokeUserLogin() {
    if (!await isUserLogin()) {
        loginSDK.open({
            withoutClose: true,
        }).then(async() => {
            window.location.reload();
        })
        // 调用登录成功
        return true;
    }
    // 未调用登录
    return false;
}

/**
 * 免费版升级
 * @returns true 代表后续流程继续
 */
export async function invokeEditionUpgrade(useWhere: UsageAmountWhere, version: EurekaVersion, trackParams: Record<string, any> = {}) {
    const biTrackService = injectService<BiTrackService>(BiTrackService);
    // 埋点
    biTrackService.biTrack(EurekaTrackEventType.SS_EUREKA_UPGRADE_PAGE, {
        sourceType: 'upgrade_trigger',
        eventDetailStr: {
            product_line: 'eureka_home',
            trigger_source: 'paywall',
            // 通过window 获取href 获取['search-type']
            agent_name: get(window.location.href.split('?')[1], 'search-type', 'tech-qa'),
            paidType: 'enterprise',
            ...trackParams,
        },
    });
    if (!await isUserLogin()) {
        loginSDK.open({
            withoutClose: true,
        }).then(async() => {
            window.location.reload();
        })
        return false;
    }
    const [packages, { freemium }] = await Promise.all([packageInfoDeferred.promise, controlDeferred.promise]);
    const ifNeedOpen = getIfHasTrialPackage(packages) || freemium;

    // 使用次数超限弹窗
    if (ifNeedOpen) {
        const packageType = getPackageTypeByVersion(version);
        openEditionUpgrade({ usageType: UpgradeUsageType.TRY_PREMIUM, useWhere, packageType, trackParams });
        return false;
    }
    return true;
}

/**
 * 判断用量受限
 * techSeek: 90164076
 * techMind: 90164072
 * techRisk: 90164082
 * techResearch: 90112037
 * idea: 90112019
 * @param errorCode
 * @returns
 */
export function prepareInterceptorEdition(code: number | string) {
    return [90112037, 90164072, 90164076, 90164082, 1510001001, 90180009, 90112019, 90210004].includes(Number(code));
}
